
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface BetaFeedbackWidgetProps {
  pageTitle?: string;
}

export default function BetaFeedbackWidget({ pageTitle }: BetaFeedbackWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    feedbackType: 'general',
    rating: 0,
    title: '',
    message: ''
  });

  // Comment out user check - show widget for everyone
  /*
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
      }
    };

    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
    });

    return () => subscription.unsubscribe();
  }, []);
  */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      alert('Du musst eingeloggt sein, um Feedback zu geben.');
      return;
    }

    if (!formData.message.trim()) {
      return;
    }

    setLoading(true);

    try {
      // Browser und Device Informationen sammeln
      const browserInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine
      };

      const deviceInfo = {
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        pixelRatio: window.devicePixelRatio
      };

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Keine gültige Session');
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/beta-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          action: 'submitFeedback',
          pageUrl: window.location.href,
          feedbackType: formData.feedbackType,
          rating: formData.rating || null,
          title: formData.title.trim() || pageTitle || 'Feedback',
          message: formData.message.trim(),
          browserInfo,
          deviceInfo
        })
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsOpen(false);
          setIsSubmitted(false);
          setFormData({
            feedbackType: 'general',
            rating: 0,
            title: '',
            message: ''
          });
        }, 2000);
      } else {
        alert(data.message || 'Fehler beim Senden des Feedbacks');
      }
    } catch (error) {
      console.error('Feedback Error:', error);
      alert('Fehler beim Senden des Feedbacks');
    } finally {
      setLoading(false);
    }
  };

  // Comment out user check - show widget for everyone
  /*
  if (!user) {
    return null;
  }
  */

  return (
    <>
      {/* Floating Feedback Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-orange-500 to-amber-500 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 z-50 cursor-pointer"
        title="Beta Feedback geben"
      >
        <i className="ri-feedback-line text-xl"></i>
      </button>

      {/* Feedback Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {!isSubmitted ? (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <i className="ri-flask-fill text-2xl"></i>
                      <div>
                        <h3 className="font-bold text-lg">Beta Feedback</h3>
                        <p className="text-orange-100 text-sm">Hilf uns dabei, Learnsnap zu verbessern</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="text-orange-200 hover:text-white transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-xl"></i>
                    </button>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                  {/* Feedback Type */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Art des Feedbacks
                    </label>
                    <select
                      value={formData.feedbackType}
                      onChange={(e) => setFormData({...formData, feedbackType: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    >
                      <option value="general">Allgemeines Feedback</option>
                      <option value="bug">Bug / Fehler</option>
                      <option value="feature">Feature-Wunsch</option>
                      <option value="ui">Design / Benutzeroberfläche</option>
                      <option value="performance">Performance / Geschwindigkeit</option>
                      <option value="other">Sonstiges</option>
                    </select>
                  </div>

                  {/* Rating */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Wie gefällt dir diese Seite?
                    </label>
                    <div className="flex items-center space-x-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFormData({...formData, rating: star})}
                          className={`text-2xl transition-colors cursor-pointer ${
                            star <= formData.rating 
                              ? 'text-yellow-400' 
                              : 'text-gray-300 hover:text-yellow-300'
                          }`}
                        >
                          <i className="ri-star-fill"></i>
                        </button>
                      ))}
                      {formData.rating > 0 && (
                        <span className="text-sm text-gray-600 ml-2">
                          ({formData.rating}/5 Sterne)
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Kurze Zusammenfassung (optional)
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder="z.B. Upload funktioniert nicht"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Dein Feedback *
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      rows={4}
                      placeholder="Beschreibe dein Feedback, gefundene Bugs oder Verbesserungsvorschläge..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Mindestens 10 Zeichen erforderlich
                    </p>
                  </div>

                  {/* Current Page Info */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600">
                      <strong>Aktuelle Seite:</strong> {pageTitle || document.title}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {window.location.href}
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading || !formData.message.trim() || formData.message.trim().length < 10}
                    className={`w-full py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                      loading || !formData.message.trim() || formData.message.trim().length < 10
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:shadow-lg cursor-pointer'
                    }`}
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill mr-2"></i>
                        Feedback senden
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              /* Success Message */
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Feedback gesendet!</h3>
                <p className="text-gray-600 mb-4">
                  Vielen Dank für dein wertvolles Feedback. Es hilft uns dabei, Learnsnap zu verbessern.
                </p>
                <div className="flex items-center justify-center space-x-2 text-orange-600">
                  <i className="ri-heart-fill"></i>
                  <span className="text-sm font-medium">Du hilfst uns beim Wachsen!</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
